import type { Translate } from 'next-translate';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { Button } from '@/components/UI/Button';
import { Textarea } from '@/components/UI/Textarea';
import { useClickOutside, useGetVideoGenModels, useKeyPress } from '@/hooks';
import {
	GenerateLandingLinkService,
	GoogleService,
	LocalStorageService,
} from '@/services';
import { type HeroTextToVideoData } from '@/types';
import { GOOGLE_EVENTS, LOCAL_STORAGE_KEYS } from '@/constants';
import CheckIcon from '@/public/icons/check-1.svg';
import bgMobile from '@/public/images/landing/backgrounds/bg-abstract-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg-abstract.webp';
import styles from './HeroTextToVideo.module.scss';

const sanitizePrompt = (value: string): string =>
	value.replace(/[<>'"&]/g, '').trim();

interface Props {
	data: HeroTextToVideoData;
	t: Translate;
	isLoading: boolean;
	funnelLinkService?: string;
}

export const HeroTextToVideo: React.FC<Props> = ({
	data,
	t,
	isLoading,
	funnelLinkService,
}) => {
	const { title, description, buttonText, data: info } = data;

	const { register, watch, setValue } = useForm<{ prompt: string }>({
		defaultValues: { prompt: '' },
	});
	const prompt = watch('prompt');
	const sanitizedPrompt = sanitizePrompt(prompt);

	const hasRestoredPrompt = React.useRef(false);
	const promptRegister = register('prompt');
	const promptRegisterWithRestore = {
		...promptRegister,
		ref: (el: HTMLTextAreaElement | null) => {
			promptRegister.ref(el);
			if (el?.value && !hasRestoredPrompt.current) {
				hasRestoredPrompt.current = true;
				setValue('prompt', el.value);
			}
		},
	};

	const { data: models } = useGetVideoGenModels();

	const [selectedModelId, setSelectedModelId] = React.useState<string | null>(
		null,
	);

	const defaultModel = React.useMemo(() => {
		const targetName = info.modelName.toLowerCase();
		const searchName =
			targetName.includes('minimax') || targetName.includes('hailuo')
				? 'minimax'
				: targetName;
		return (
			models?.find(model => model.title.toLowerCase().includes(searchName)) ??
			models?.[0]
		);
	}, [models, info.modelName]);

	const selectedModel = React.useMemo(() => {
		if (!selectedModelId) return defaultModel;
		return models?.find(model => model.id === selectedModelId) ?? defaultModel;
	}, [models, selectedModelId, defaultModel]);

	const orderedModels = React.useMemo(() => {
		if (!models?.length || !defaultModel) return models;
		return [
			defaultModel,
			...models.filter(model => model.id !== defaultModel.id),
		];
	}, [models, defaultModel]);

	const [isModelListOpen, setIsModelListOpen] = React.useState(false);
	const modelPillRef = React.useRef<HTMLButtonElement | null>(null);
	const modelListRef = React.useRef<HTMLDivElement | null>(null);

	const closeModelList = React.useCallback(() => setIsModelListOpen(false), []);

	useClickOutside([modelPillRef.current, modelListRef.current], closeModelList);
	useKeyPress('Escape', closeModelList, isModelListOpen);

	const [modelListMaxHeight, setModelListMaxHeight] = React.useState<
		number | undefined
	>(undefined);

	React.useLayoutEffect(() => {
		if (!isModelListOpen) return;

		const viewportEdgeGap = 16;

		const updateMaxHeight = () => {
			const pillRect = modelPillRef.current?.getBoundingClientRect();
			if (!pillRect) return;

			const availableHeight =
				window.innerHeight - pillRect.bottom - viewportEdgeGap;
			setModelListMaxHeight(Math.min(availableHeight, 350));
		};

		updateMaxHeight();
		window.addEventListener('resize', updateMaxHeight);
		window.addEventListener('scroll', updateMaxHeight, true);

		return () => {
			window.removeEventListener('resize', updateMaxHeight);
			window.removeEventListener('scroll', updateMaxHeight, true);
		};
	}, [isModelListOpen]);

	const handleSelectModel = (modelId: string) => {
		setSelectedModelId(modelId);
		closeModelList();
	};

	React.useEffect(() => {
		LocalStorageService.set(LOCAL_STORAGE_KEYS.VIDEO_GEN_LANDING_DATA, {
			prompt: sanitizedPrompt,
			modelName: selectedModel?.title ?? info.modelName,
		});
	}, [sanitizedPrompt, selectedModel, info.modelName]);

	const handleGenerate = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);

		window.location.href =
			funnelLinkService || GenerateLandingLinkService.webApp();
	};

	return (
		<section className={`${styles['hero']}`}>
			<MyImage
				imageProps={{
					src: bgMobile,
					alt: 'background image',
					title: `${t(title)}`,
					priority: true,
					fetchPriority: 'low',
				}}
				settings={{
					classes: {
						picture: `${styles['hero__background']}`,
						image: styles['hero__background-img'],
					},
				}}
			>
				<source srcSet={bg.src} media='(min-width: 1023px)' />
			</MyImage>
			<Container className={`${styles['hero__container']}`}>
				<div className={`${styles['hero__info']}`}>
					<h1 className={`${styles['hero__title']}`}>{t(title)}</h1>
					<p className={`${styles['hero__subtitle']}`}>{t(description)}</p>
				</div>

				<div className={`${styles['hero__card']}`}>
					<Textarea
						register={promptRegisterWithRestore}
						placeholder={t(info.inputPlaceholder)}
						disabled={isLoading}
						classes={{
							field: styles['hero__textarea-field'],
							textarea: styles['hero__textarea'],
						}}
					/>

					<div className={styles['hero__toolbar']}>
						<div className={styles['hero__model-wrapper']}>
							<button
								ref={modelPillRef}
								type='button'
								className={styles['hero__model-pill']}
								disabled={isLoading || !models?.length}
								aria-expanded={isModelListOpen}
								aria-haspopup='listbox'
								onClick={() => setIsModelListOpen(prev => !prev)}
							>
								<div className={styles['hero__model-bg']}>
									{selectedModel?.icon ? (
										<img
											className={styles['hero__model-icon']}
											src={selectedModel.icon}
											alt={selectedModel.title}
										/>
									) : (
										<></>
									)}
								</div>

								<span className={styles['hero__model-name']}>
									{selectedModel?.title ?? info.modelName}
								</span>
							</button>

							{isModelListOpen && !!orderedModels?.length && (
								<div
									ref={modelListRef}
									className={styles['models-list']}
									role='listbox'
									style={{ maxHeight: modelListMaxHeight }}
								>
									{orderedModels.map(model => {
										const isSelected = model.id === selectedModel?.id;

										return (
											<button
												key={model.id}
												type='button'
												role='option'
												aria-selected={isSelected}
												disabled={isLoading}
												className={`${styles['model-item']} ${
													isSelected ? styles['model-item__selected'] : ''
												}`}
												onClick={() => handleSelectModel(model.id)}
											>
												<div className={styles['model-item__icon_container']}>
													{model.icon ? (
														<img
															className={styles['model-item__preview']}
															src={model.icon}
															alt={model.title}
														/>
													) : (
														<></>
													)}
												</div>

												<span className={styles['model-item__name']}>
													{model.title}
												</span>

												{isSelected && (
													<CheckIcon
														className={styles['model-item__checked']}
													/>
												)}
											</button>
										);
									})}
								</div>
							)}
						</div>
					</div>
				</div>

				<div className={`${styles['hero__generate-wrapper']}`}>
					<Button
						as='button'
						preset='landing'
						text={t(buttonText)}
						size='large'
						fullWidth
						classes={{
							button: styles['hero__generate-button'],
							disabled: styles['hero__generate-button_disabled'],
						}}
						onClick={handleGenerate}
					/>
				</div>

				<div className={styles['hero__trust-badges']}>
					<span className={styles['hero__trust-badge']}>
						<span>⭐️</span>
						{t('heroData_trustBadge1')}
					</span>
					<span className={styles['hero__trust-badge']}>
						<span>⭐️</span>
						{t('heroData_trustBadge2')}
					</span>
				</div>
			</Container>
		</section>
	);
};
